// Förifyllnad av samordningsmötets agenda från projektets eget innehåll.
// Läser risksammanställningar, AMP, arbetsberedningar, entreprenörer och tillbud
// så att Bas-P slipper skriva om underlag som redan finns i systemet.
import { supabase } from "@/integrations/supabase/client";
import { fragorData } from "@/data/fragorData";

export interface MeetingDecision {
  id: string;
  text: string;
  responsible?: string;
  dueDate?: string;
  done?: boolean;
  fromMeeting?: string;
}

export interface CoordinationPrefill {
  content: Record<string, string>;
  decisions: MeetingDecision[];
  /** Kort sammanfattning av vad som saknar underlag inför mötet. */
  gaps: string[];
}

const questionText = (id: string) => fragorData.find((f) => f.id === id)?.text || id;

const listOf = (rows: string[], max = 12) => {
  if (!rows.length) return "";
  const shown = rows.slice(0, max);
  const rest = rows.length - shown.length;
  return shown.map((r) => `• ${r}`).join("\n") + (rest > 0 ? `\n• … och ${rest} till` : "");
};

export const readDecisions = (content: any): MeetingDecision[] => {
  const raw = content?.decisions;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d) => d && typeof d === "object")
    .map((d: any) => ({
      id: String(d.id ?? crypto.randomUUID()),
      text: String(d.text ?? ""),
      responsible: d.responsible ? String(d.responsible) : "",
      dueDate: d.dueDate ? String(d.dueDate) : "",
      done: Boolean(d.done),
      fromMeeting: d.fromMeeting ? String(d.fromMeeting) : undefined,
    }));
};

/**
 * Bygger agendaunderlag för ett nytt samordningsmöte.
 * `since` = datum för föregående möte; används för "ändringar sedan förra mötet".
 */
export async function buildCoordinationPrefill(
  projectId: string,
  since?: string | null
): Promise<CoordinationPrefill> {
  const sinceIso = since ? new Date(since).toISOString() : null;

  const [risks, amp, workPreps, contractors, incidents, meetings] = await Promise.all([
    supabase
      .from("folder_risk_assessments")
      .select("name, data, updated_at")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .eq("phase", "byggskede"),
    supabase
      .from("work_environment_plans")
      .select("id, name, updated_at, data")
      .eq("project_id", projectId)
      .order("updated_at", { ascending: false })
      .limit(1),
    supabase
      .from("work_preparations")
      .select("name, review_status, updated_at, data")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false }),
    supabase.from("stage_contractors").select("company, scope, is_bas_u").eq("project_id", projectId),
    supabase
      .from("incident_reports")
      .select("incident_type, description, occurred_at, status")
      .eq("project_id", projectId)
      .order("occurred_at", { ascending: false })
      .limit(30),
    supabase
      .from("coordination_meetings")
      .select("meeting_date, content_json")
      .eq("project_id", projectId)
      .order("meeting_date", { ascending: false }),
  ]);

  const gaps: string[] = [];

  // 1. Öppna risker från risksammanställningarna
  const openRisks: string[] = [];
  for (const a of risks.data ?? []) {
    const svar = (((a as any).data || {}).svar || {}) as Record<string, any>;
    for (const [id, v] of Object.entries(svar)) {
      if (String((v as any)?.svar || "").toLowerCase() !== "ja") continue;
      const atgard = String((v as any)?.atgard || "").trim();
      if (atgard) continue; // risken har redan en åtgärd
      openRisks.push(`${questionText(id)} (${(a as any).name})`);
    }
  }
  if (!risks.data?.length) gaps.push("Ingen risksammanställning för byggskedet finns ännu.");

  // 2. AMP
  const ampRow = (amp.data ?? [])[0] as any;
  const ampLine = ampRow
    ? `Arbetsmiljöplanen "${ampRow.name}" senast uppdaterad ${new Date(
        ampRow.updated_at
      ).toLocaleDateString("sv-SE")}. Gå igenom särskilda risker och aktuella ändringar.`
    : "Ingen arbetsmiljöplan är upprättad i projektet ännu – detta måste tas upp på mötet.";
  if (!ampRow) gaps.push("Arbetsmiljöplan saknas.");

  // 3. Ändringar sedan förra mötet
  const changed = (workPreps.data ?? []).filter(
    (w: any) => !sinceIso || (w.updated_at && w.updated_at > sinceIso)
  );
  const changedRisks = (risks.data ?? []).filter(
    (a: any) => !sinceIso || (a.updated_at && a.updated_at > sinceIso)
  );
  const changeLines = [
    ...changed.map((w: any) => `Arbetsberedning uppdaterad: ${w.name}`),
    ...changedRisks.map((a: any) => `Risksammanställning uppdaterad: ${a.name}`),
  ];

  // 4. Samordning entreprenörer / samtidiga arbeten
  const contractorLines = (contractors.data ?? []).map(
    (c: any) => `${c.company}${c.is_bas_u ? " (Bas-U)" : ""}${c.scope ? ` – ${c.scope}` : ""}`
  );
  const activePreps = (workPreps.data ?? []).filter((w: any) => w.review_status === "godkand");
  const prepLines = activePreps.map((w: any) => `Pågående arbete: ${w.name}`);
  if (!contractorLines.length) gaps.push("Inga entreprenörer är registrerade på etapperna.");

  // 5. Avvikelser och tillbud
  const newIncidents = (incidents.data ?? []).filter(
    (i: any) => !sinceIso || (i.occurred_at && new Date(i.occurred_at).toISOString() > sinceIso)
  );
  const incidentLines = newIncidents.map(
    (i: any) =>
      `${new Date(i.occurred_at).toLocaleDateString("sv-SE")} – ${i.incident_type}: ${String(
        i.description || ""
      ).slice(0, 140)}`
  );

  // 6. Öppna beslut från tidigare möten
  const carried: MeetingDecision[] = [];
  for (const m of meetings.data ?? []) {
    for (const d of readDecisions((m as any).content_json)) {
      if (d.done || !d.text.trim()) continue;
      if (carried.some((c) => c.text === d.text)) continue;
      carried.push({ ...d, id: crypto.randomUUID(), fromMeeting: (m as any).meeting_date });
    }
  }

  const waitingReview = (workPreps.data ?? []).filter(
    (w: any) => w.review_status === "inskickad" || w.review_status === "komplettering"
  );
  if (waitingReview.length)
    gaps.push(`${waitingReview.length} arbetsberedning(ar) väntar på granskning.`);

  return {
    content: {
      genomgang_amp: ampLine,
      kvarvarande_risker:
        listOf(openRisks) ||
        "Inga risker utan beslutad åtgärd finns i risksammanställningarna just nu.",
      andringar: listOf(changeLines) || "Inga ändringar i handlingar registrerade sedan förra mötet.",
      samordning_ue:
        listOf([...contractorLines, ...prepLines]) ||
        "Inga entreprenörer eller pågående arbetsberedningar registrerade.",
      avvikelser: listOf(incidentLines) || "Inga tillbud eller olyckor rapporterade sedan förra mötet.",
      beslut: carried.length
        ? `Öppna punkter från tidigare möten:\n${listOf(
            carried.map((d) =>
              [d.text, d.responsible, d.dueDate].filter(Boolean).join(" – ")
            )
          )}`
        : "",
    },
    decisions: carried,
    gaps,
  };
}
